import React, { memo, useCallback, useMemo, useState } from 'react';

import { Box, Button, Typography } from '@mui/material';
import { AutocompleteCDS } from '@sharedCR/coreDesignSystem/form';
import { advisorPortalConfigurationApi, companyDomainApi, realtorsApi } from 'app/services/api';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { DomainType } from 'constants/domains';
import useCompanyId from 'hooks/useCompanyId';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';

import { PropertyAndLoanTestIds } from '../../../../../PropertyAndLoan.testIds';

import {
  CONFIG_SELECT_LABEL,
  CONFIG_SELECT_PLACEHOLDER,
  INVITE_LABEL,
  INVITED_LABEL,
} from './RealtorInviteButton.constants';
import { configSelectStyles, realtorInviteButtonStyles } from './RealtorInviteButton.styles';
import {
  getDefaultConfigId,
  isOptionEqualToValue,
  splitAgentName,
  toConfigOptions,
} from './RealtorInviteButton.utils';

// Invites a brand-new (free-typed) agent to the Realtor Portal. Shown only when the company has a
// RealtorsPortal domain configured — the loan editor doesn't load company domains into the store,
// so (like RealtorPicker fetches the directory) this fetches them per-company and degrades to
// hidden on failure. Builds the invite from the agent form's name/email/phone.
//
// When the company has more than one AdvisorPortalConfiguration, a picker lets the inviter choose
// which portal the agent is invited to; with exactly one config it's auto-selected (no picker);
// with zero configs the invite behaves exactly as before (legacy realtor invite, no config).
const INVITE_FAILED_LABEL = 'The invite could not be sent.';

const RealtorInviteButton = ({ name = '', email = '', phone = '', disabled = false }) => {
  const companyId = useCompanyId();

  const [hasPortalDomain, setHasPortalDomain] = useState(false);
  const [isInviting, setIsInviting] = useState(false);
  const [inviteError, setInviteError] = useState(null);
  const [isInvited, setIsInvited] = useState(false);
  const [configs, setConfigs] = useState([]);
  const [selectedConfigId, setSelectedConfigId] = useState(null);

  useEffectSafeStrictMode(() => {
    const lifecycle = { cancelled: false };

    const loadDomains = async () => {
      try {
        const { data } = await companyDomainApi.getCompanyDomains({ companyId });

        if (!lifecycle.cancelled) {
          setHasPortalDomain(
            (data ?? []).some(domain => domain.type === DomainType.REALTORS_PORTAL),
          );
        }
      } catch {
        if (!lifecycle.cancelled) {
          setHasPortalDomain(false);
        }
      }
    };

    const loadConfigs = async () => {
      try {
        const { data } = await advisorPortalConfigurationApi.getAdvisorPortalConfigurations();

        if (!lifecycle.cancelled) {
          const list = data ?? [];

          setConfigs(list);
          setSelectedConfigId(getDefaultConfigId(list));
        }
      } catch {
        if (!lifecycle.cancelled) {
          setConfigs([]);
          setSelectedConfigId(null);
        }
      }
    };

    loadDomains();
    loadConfigs();

    return () => {
      lifecycle.cancelled = true;
    };
  }, [companyId]);

  const configOptions = useMemo(() => toConfigOptions(configs), [configs]);
  const showConfigPicker = configs.length > 1;
  const selectedConfigOption = useMemo(
    () => configOptions.find(option => option.value === selectedConfigId) ?? null,
    [configOptions, selectedConfigId],
  );

  const { firstName, lastName } = splitAgentName(name);
  const canInvite =
    !isInvited &&
    !_.isEmpty(email) &&
    !_.isEmpty(firstName) &&
    !_.isEmpty(lastName) &&
    (!showConfigPicker || !!selectedConfigId);

  const onChangeConfig = useCallback((event, option) => {
    setSelectedConfigId(option?.value ?? null);
  }, []);

  const onInvite = useCallback(async () => {
    setIsInviting(true);
    setInviteError(null);

    try {
      await realtorsApi.createRealtorInvite({
        firstName,
        lastName,
        email,
        phoneNumber: phone,
        advisorPortalConfigurationId: selectedConfigId ?? null,
      });
      setIsInvited(true);
    } catch (error) {
      setIsInvited(false);
      setInviteError(error?.response?.data?.error?.message ?? INVITE_FAILED_LABEL);
    } finally {
      setIsInviting(false);
    }
  }, [firstName, lastName, email, phone, selectedConfigId]);

  if (!hasPortalDomain) {
    return null;
  }

  return (
    <Box sx={realtorInviteButtonStyles}>
      {showConfigPicker && (
        <Box sx={configSelectStyles}>
          <AutocompleteCDS
            disableClearable
            dataTestId={PropertyAndLoanTestIds.REALTOR_INVITE_CONFIG_SELECT}
            disabled={disabled || isInviting || isInvited}
            isOptionEqualToValue={isOptionEqualToValue}
            label={CONFIG_SELECT_LABEL}
            name="advisorPortalConfigurationId"
            optionKey="value"
            options={configOptions}
            placeholder={CONFIG_SELECT_PLACEHOLDER}
            value={selectedConfigOption}
            onChange={onChangeConfig}
          />
        </Box>
      )}

      <Button
        data-testid={PropertyAndLoanTestIds.REALTOR_INVITE_BUTTON}
        disabled={disabled || isInviting || !canInvite}
        size="small"
        variant="line"
        onClick={onInvite}
      >
        {isInvited ? INVITED_LABEL : INVITE_LABEL}
      </Button>

      {inviteError && (
        <Typography
          color="error.main"
          data-testid={PropertyAndLoanTestIds.REALTOR_INVITE_ERROR}
          variant="regular12"
        >
          {inviteError}
        </Typography>
      )}
    </Box>
  );
};

RealtorInviteButton.propTypes = {
  name: PropTypes.string,
  email: PropTypes.string,
  phone: PropTypes.string,
  disabled: PropTypes.bool,
};

export default memo(RealtorInviteButton);
